import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  getCountFromServer,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type { LinkPage, LinkPageLink } from '@/types';
import { generateShortCode } from '@/utils/deeplinkGenerator';

const COLLECTION_NAME = 'linkpages';

export interface CreateLinkPageInput {
  title: string;
  description?: string;
  userId: string;
}

export interface UpdateLinkPageInput {
  title?: string;
  description?: string;
  photoUrl?: string | null;
  headerImageUrl?: string | null;
  status?: 'active' | 'disabled';
}

// Generate a unique code for linkpages (checks linkpages collection only)
const generateUniqueLinkPageCode = async (): Promise<string> => {
  let code = generateShortCode();
  let attempts = 0;
  const maxAttempts = 10;

  while (attempts < maxAttempts) {
    const q = query(collection(db, COLLECTION_NAME), where('code', '==', code));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return code;
    code = generateShortCode();
    attempts++;
  }

  throw new Error('Failed to generate unique code');
};

// Create a new linkpage
export const createLinkPage = async (input: CreateLinkPageInput): Promise<LinkPage> => {
  const { title, description, userId } = input;
  const code = await generateUniqueLinkPageCode();

  const docRef = await addDoc(collection(db, COLLECTION_NAME), {
    userId,
    code,
    title,
    description: description || null,
    photoUrl: null,
    links: [],
    theme: null,
    status: 'active',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await updateDoc(docRef, { id: docRef.id });

  const docSnap = await getDoc(docRef);
  return { id: docRef.id, ...docSnap.data() } as LinkPage;
};

// Get a linkpage by ID
export const getLinkPageById = async (id: string): Promise<LinkPage | null> => {
  const docRef = doc(db, COLLECTION_NAME, id);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() } as LinkPage;
};

// Get all linkpages for a user
export const getUserLinkPages = async (userId: string): Promise<LinkPage[]> => {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })) as LinkPage[];
};

// Update a linkpage
export const updateLinkPage = async (id: string, input: UpdateLinkPageInput): Promise<LinkPage> => {
  const docRef = doc(db, COLLECTION_NAME, id);
  const updateData: Record<string, unknown> = { updatedAt: serverTimestamp() };

  if (input.title !== undefined) updateData.title = input.title;
  if (input.description !== undefined) updateData.description = input.description;
  if (input.photoUrl !== undefined) updateData.photoUrl = input.photoUrl;
  if (input.headerImageUrl !== undefined) updateData.headerImageUrl = input.headerImageUrl;
  if (input.status !== undefined) updateData.status = input.status;

  await updateDoc(docRef, updateData);
  const docSnap = await getDoc(docRef);
  return { id: docSnap.id, ...docSnap.data() } as LinkPage;
};

// Delete a linkpage
export const deleteLinkPage = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, COLLECTION_NAME, id));
};

// Add a link to a linkpage
export const addLinkToPage = async (
  pageId: string,
  link: Omit<LinkPageLink, 'id' | 'order'>
): Promise<LinkPage> => {
  const docRef = doc(db, COLLECTION_NAME, pageId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) throw new Error('Link page not found');

  const data = docSnap.data();
  const links: LinkPageLink[] = data.links || [];

  const newLink: LinkPageLink = {
    ...link,
    id: `link_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    order: links.length,
  };

  await updateDoc(docRef, {
    links: [...links, newLink],
    updatedAt: serverTimestamp(),
  });

  const updated = await getDoc(docRef);
  return { id: updated.id, ...updated.data() } as LinkPage;
};

// Remove a link from a linkpage
export const removeLinkFromPage = async (pageId: string, linkId: string): Promise<LinkPage> => {
  const docRef = doc(db, COLLECTION_NAME, pageId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) throw new Error('Link page not found');

  const data = docSnap.data();
  const links: LinkPageLink[] = (data.links || []).filter(
    (l: LinkPageLink) => l.id !== linkId
  );

  // Reorder remaining links
  const reordered = links.map((l, i) => ({ ...l, order: i }));

  await updateDoc(docRef, {
    links: reordered,
    updatedAt: serverTimestamp(),
  });

  const updated = await getDoc(docRef);
  return { id: updated.id, ...updated.data() } as LinkPage;
};

// Reorder links in a linkpage
export const reorderLinks = async (pageId: string, linkIds: string[]): Promise<LinkPage> => {
  const docRef = doc(db, COLLECTION_NAME, pageId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) throw new Error('Link page not found');

  const data = docSnap.data();
  const existingLinks: LinkPageLink[] = data.links || [];
  const linkMap = new Map(existingLinks.map((l) => [l.id, l]));

  const reordered = linkIds
    .map((id, i) => {
      const link = linkMap.get(id);
      if (!link) return null;
      return { ...link, order: i };
    })
    .filter(Boolean) as LinkPageLink[];

  await updateDoc(docRef, {
    links: reordered,
    updatedAt: serverTimestamp(),
  });

  const updated = await getDoc(docRef);
  return { id: updated.id, ...updated.data() } as LinkPage;
};

// Get view count for a linkpage from Firestore sub-collection
export const getLinkPageViews = async (id: string): Promise<number> => {
  const viewsCol = collection(db, COLLECTION_NAME, id, 'views');
  const snapshot = await getCountFromServer(viewsCol);
  return snapshot.data().count;
};

// Toggle linkpage disabled/active
export const toggleLinkPageStatus = async (id: string): Promise<LinkPage> => {
  const docRef = doc(db, COLLECTION_NAME, id);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) throw new Error('Link page not found');

  const current = docSnap.data();
  const newStatus = current.status === 'disabled' ? 'active' : 'disabled';

  await updateDoc(docRef, {
    status: newStatus,
    updatedAt: serverTimestamp(),
  });

  const updated = await getDoc(docRef);
  return { id: updated.id, ...updated.data() } as LinkPage;
};
